import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { TopNav, Footer } from '../components/PublicLayout'
import SEO from '../components/SEO'

// ─── FAQ page ────────────────────────────────────────────────────────────────
//
// Two audiences, one page:
//   1. Bookkeepers evaluating Receipt Chaser (prospects)
//   2. Clients who've received a receipt request from their bookkeeper
//
// Two big "I'm a..." buttons at the top jump to the relevant section so
// neither audience has to scroll past content that isn't for them. Both
// sections are still rendered on the page so search engines see everything
// and deep links like /faq#clients work.
//
// SEO notes:
//   - Questions are worded to match real search queries where natural
//     (e.g. "How long do I need to keep business receipts in the UK?").
//   - The page emits JSON-LD FAQPage schema so Google can show Q&As as
//     rich results in search.
//   - Each question's answer is plain text in the JSON-LD (no JSX), so
//     where the on-page answer contains a Link we keep a parallel plain
//     string for the schema.

const CONTACT_EMAIL = 'hello@receiptchaser.com'

// Shared style for inline links inside FAQ answers. Declared up here (not at
// the bottom of the file) so the FAQ data arrays below can reference it
// safely. `const` is hoisted but not initialised, so using it before this
// line would throw a "Cannot access before initialization" error.
const faqLinkStyle = { color: 'var(--accent)', fontWeight: 500 }

// ─── Question data ───────────────────────────────────────────────────────────
//
// Each entry has:
//   q:       the question (string)
//   a:       the on-page answer (string or JSX with links)
//   aPlain:  the same answer as plain text, for JSON-LD schema
//            (only required when `a` is JSX; falls back to `a` otherwise)

const BOOKKEEPER_FAQS = [
  {
    q: 'What is receipt collection software for bookkeepers?',
    a: (
      <>
        Receipt collection software lets a bookkeeper send their client a
        list of transactions needing a receipt or invoice. The client uploads
        each one in one place instead of replying piecemeal to emails or
        WhatsApp messages. Receipt Chaser does this for UK bookkeeping
        practices.{' '}
        <Link to="/features" style={faqLinkStyle}>See how each part works</Link>.
      </>
    ),
    aPlain: "Receipt collection software lets a bookkeeper send their client a list of transactions needing a receipt or invoice. The client uploads each one in one place instead of replying piecemeal to emails or WhatsApp messages. Receipt Chaser does this for UK bookkeeping practices.",
  },
  {
    q: 'How do I chase missing receipts from clients?',
    a: 'Send each client a single link with the transactions you need receipts for, rather than chasing each one over email. Receipt Chaser turns your accounting data into a structured list the client works through in their own time, uploading a photo, PDF, or screenshot against each transaction. You see what is outstanding without having to ask.',
  },
  {
    q: 'Does it work with Xero, Sage, QuickBooks, and FreeAgent?',
    a: 'Yes, all four. Export a CSV of the transactions needing receipts from your accounting software and import it into Receipt Chaser. It also accepts plain UK bank exports if you are working from those instead.',
  },
  {
    q: 'How long does it take to set up Receipt Chaser?',
    a: 'About 10 minutes for the first time. Create your account, add a client, paste in a CSV of transactions, and send the first request. Your client receives the email straight away.',
  },
  {
    q: 'Do my clients need to create an account?',
    a: "No. Clients click a unique link in the email you send, upload their receipts, and they are done. No password, no sign-up, no app to install. They can upload from their phone, tablet, or laptop.",
  },
  {
    q: 'What if a client cannot find a receipt?',
    a: 'They can mark the transaction as a lost receipt with a brief explanation of what it was for. This creates a printable PDF declaration you can keep with the client\'s records, which is the standard HMRC-friendly way to handle missing receipts.',
  },
  {
    q: 'Will HMRC accept scanned or photographed receipts?',
    a: 'Yes. HMRC accepts digital copies of receipts and invoices as long as they are legible and contain all the information from the original. The exceptions are documents showing non-VAT tax like dividend vouchers, which must be kept in their original format. Receipt Chaser stores the digital copies your clients upload so you have a clear audit trail.',
  },
  {
    q: 'How long do I need to keep business receipts in the UK?',
    a: 'HMRC requires businesses to keep records for at least six years from the end of the relevant accounting period. Some situations need longer retention, for example when a transaction covers more than one accounting period or HMRC has started a compliance check. Receipt Chaser keeps each uploaded file for seven years from the date it was uploaded, so the six-year HMRC requirement is comfortably covered.',
  },
  {
    q: 'Can multiple people from my practice use Receipt Chaser?',
    a: 'Yes, every plan includes unlimited practice users at no extra cost. Add your junior, admin, or whoever else needs access. Everyone sees the same client list and request status.',
  },
  {
    q: 'Is Receipt Chaser GDPR-compliant?',
    a: (
      <>
        Yes. We process personal data lawfully under the UK GDPR, keep it
        secure, and only use it to deliver the service. Full details are in
        our{' '}
        <Link to="/privacy" style={faqLinkStyle}>privacy policy</Link>.
      </>
    ),
    aPlain: 'Yes. We process personal data lawfully under the UK GDPR, keep it secure, and only use it to deliver the service. Full details are in our privacy policy.',
  },
  {
    q: 'Is there a free trial?',
    a: (
      <>
        Yes, 30 days. No card required to start, and you get full feature
        access. See the details on our{' '}
        <Link to="/pricing" style={faqLinkStyle}>pricing page</Link>.
      </>
    ),
    aPlain: 'Yes, 30 days. No card required to start, and you get full feature access. See the details on our pricing page.',
  },
  {
    q: 'How much does Receipt Chaser cost?',
    a: (
      <>
        Two plans: £3 per client per month, or £25 flat for unlimited
        clients. No setup fees, no per-seat charges. Full breakdown on the{' '}
        <Link to="/pricing" style={faqLinkStyle}>pricing page</Link>.
      </>
    ),
    aPlain: 'Two plans: £3 per client per month, or £25 flat for unlimited clients. No setup fees, no per-seat charges. Full breakdown on the pricing page.',
  },
  {
    q: "What if I have a question that's not answered here?",
    a: (
      <>
        Drop us a line on the{' '}
        <Link to="/contact" style={faqLinkStyle}>contact page</Link>
        {' '}and we will get back to you shortly.
      </>
    ),
    aPlain: 'Drop us a line on the contact page and we will get back to you shortly.',
  },
]

const CLIENT_FAQS = [
  {
    q: 'I got an email asking me to upload receipts. Is this legitimate?',
    a: "If your bookkeeper uses Receipt Chaser, they will have sent you a link to upload receipts and invoices for recent transactions. The email will come from your bookkeeper's practice and the link will take you to a page that lists the transactions they need receipts for. If you are unsure, contact your bookkeeper directly to confirm before clicking anything.",
  },
  {
    q: 'Why does my bookkeeper need these receipts?',
    a: "Your bookkeeper needs a receipt or invoice for each business expense to keep your records accurate and HMRC-compliant. Without one, the expense cannot be properly recorded, which can mean a higher tax bill or problems if HMRC ever asks for evidence of a transaction.",
  },
  {
    q: 'Do I need to create an account or remember a password?',
    a: "No. The link in your email takes you straight to a page showing the transactions your bookkeeper needs receipts for. You can upload files directly with no account or password needed.",
  },
  {
    q: 'What if I cannot find a receipt?',
    a: "There is a mark-as-lost option on each transaction. You will be asked to briefly explain what the expense was for. This creates a record your bookkeeper can use for HMRC purposes if needed. Use it when you genuinely cannot find the receipt. Your bookkeeper would rather you mark it as lost than guess or skip it.",
  },
  {
    q: 'What file formats can I upload?',
    a: "Photos, PDFs, screenshots, scanned documents, anything that shows the receipt or invoice clearly. Email confirmations and order confirmations from online purchases work too. The main thing is that the date, amount, supplier, and what was bought are all readable.",
  },
  {
    q: 'Can I upload receipts from my phone?',
    a: "Yes. The upload page works on phone, tablet, or laptop. On a phone you can take a photo of a paper receipt directly through the upload screen, no need to email it to yourself first.",
  },
  {
    q: 'Is my information safe?',
    a: (
      <>
        Yes. Files are stored securely and only your bookkeeper has access
        to them. Each file is kept for seven years from the date it was
        uploaded, which comfortably covers HMRC record-keeping rules.
        Full details are in our{' '}
        <Link to="/privacy" style={faqLinkStyle}>privacy policy</Link>.
      </>
    ),
    aPlain: 'Yes. Files are stored securely and only your bookkeeper has access to them. Each file is kept for seven years from the date it was uploaded, which comfortably covers HMRC record-keeping rules. Full details are in our privacy policy.',
  },
  {
    q: "I'm having trouble uploading a file. What should I do?",
    a: "First, check the file is not unusually large (over about 10 MB). If it is, try taking a fresh photo or saving the PDF at a smaller size. If that does not help, contact your bookkeeper directly. They will be able to help, or get in touch with us on their behalf if it is a technical issue with Receipt Chaser.",
  },
  {
    q: 'Do I have to do this all at once?',
    a: "No. You can upload some now and come back to the rest later using the same link. Your progress is saved automatically. Your bookkeeper can see what has been uploaded and what is still outstanding, so they know what they are waiting on.",
  },
  {
    q: "Who do I contact if I'm still stuck?",
    a: (
      <>
        Your bookkeeper is the best first point of contact, since they sent
        you the request and know your specific situation. If it is a
        technical issue they cannot help with, they can reach us directly,
        or you can email us at{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} style={faqLinkStyle}>{CONTACT_EMAIL}</a>.
      </>
    ),
    aPlain: `Your bookkeeper is the best first point of contact, since they sent you the request and know your specific situation. If it is a technical issue they cannot help with, they can reach us directly, or you can email us at ${CONTACT_EMAIL}.`,
  },
]

// ─── JSON-LD FAQPage schema ──────────────────────────────────────────────────
// Lets Google show questions as expandable answers in search results.
// Both sections combined into one schema so the full page is represented.

function buildFaqJsonLd() {
  const allFaqs = [...BOOKKEEPER_FAQS, ...CLIENT_FAQS]
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: allFaqs.map(({ q, a, aPlain }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: aPlain || (typeof a === 'string' ? a : ''),
      },
    })),
  }
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function FAQ() {
  useEffect(() => {
    // Honour anchor on initial load (so /faq#clients works)
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1))
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      window.scrollTo(0, 0)
    }
  }, [])

  const jumpTo = (id) => {
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      // Update URL without a full navigation
      history.replaceState(null, '', `#${id}`)
    }
  }

  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh' }}>
      <SEO
        title="FAQ: receipt collection for UK bookkeepers"
        description="Common questions about Receipt Chaser, the receipt collection tool for UK bookkeepers. Setup, pricing, integrations, HMRC compliance, and client guidance."
        path="/faq"
        jsonLd={buildFaqJsonLd()}
      />
      <TopNav />

      {/* Intro */}
      <section style={{ padding: '64px 0 32px' }}>
        <div className="container" style={{
          padding: '0 24px', maxWidth: 760, textAlign: 'center',
        }}>
          <p style={{
            fontSize: '0.78rem', fontWeight: 600,
            color: 'var(--accent)', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: 12,
          }}>
            Frequently asked questions
          </p>
          <h1 style={{
            marginBottom: 18, lineHeight: 1.15,
            fontSize: 'clamp(2rem, 5vw, 2.6rem)',
          }}>
            Pick the section that fits you.
          </h1>
          <p style={{ color: 'var(--ink-muted)', fontSize: '1.05rem', lineHeight: 1.6 }}>
            Two audiences use Receipt Chaser: bookkeepers running the
            practice, and their clients sending in receipts. Different
            questions, different answers. Jump to the right section below.
          </p>
        </div>
      </section>

      {/* Big audience-picker buttons */}
      <section style={{ padding: '8px 0 48px' }}>
        <div className="container" style={{ padding: '0 24px', maxWidth: 760 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 16,
          }}>
            <AudienceButton
              icon={<BookkeeperIcon />}
              label="I'm a bookkeeper"
              sub="Evaluating Receipt Chaser for my practice"
              onClick={() => jumpTo('bookkeepers')}
            />
            <AudienceButton
              icon={<ClientIcon />}
              label="I'm a client"
              sub="My bookkeeper has asked me to upload receipts"
              onClick={() => jumpTo('clients')}
            />
          </div>
        </div>
      </section>

      {/* Bookkeeper section */}
      <FAQSection
        id="bookkeepers"
        eyebrow="For bookkeepers"
        heading="Questions from bookkeepers evaluating Receipt Chaser."
        items={BOOKKEEPER_FAQS}
        background="var(--white)"
      />

      {/* Client section */}
      <FAQSection
        id="clients"
        eyebrow="For clients"
        heading="Questions from clients who've received a receipt request."
        items={CLIENT_FAQS}
        background="var(--surface)"
      />

      {/* Final still-stuck note */}
      <section style={{ padding: '48px 0 80px', background: 'var(--white)' }}>
        <div className="container" style={{
          padding: '0 24px', maxWidth: 600, textAlign: 'center',
        }}>
          <h2 style={{
            marginBottom: 12, lineHeight: 1.2,
            fontSize: 'clamp(1.4rem, 4vw, 1.7rem)',
          }}>
            Still have a question?
          </h2>
          <p style={{
            color: 'var(--ink-muted)', fontSize: '0.98rem',
            lineHeight: 1.6, marginBottom: 22,
          }}>
            If we have not covered it here, just ask. We read every message
            and reply as soon as we can.
          </p>
          <Link
            to="/contact"
            className="btn btn-primary"
            style={{ padding: '12px 28px', fontSize: '0.95rem' }}
          >
            Get in touch →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  )
}

// ─── Bits ────────────────────────────────────────────────────────────────────

function AudienceButton({ icon, label, sub, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'var(--white)',
        border: '1.5px solid var(--surface-2)',
        borderRadius: 'var(--radius-lg)',
        padding: '22px 24px',
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: 18,
        transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
        fontFamily: 'inherit',
        width: '100%',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'var(--accent)'
        e.currentTarget.style.boxShadow = 'var(--shadow-md)'
        e.currentTarget.style.transform = 'translateY(-2px)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'var(--surface-2)'
        e.currentTarget.style.boxShadow = 'none'
        e.currentTarget.style.transform = 'translateY(0)'
      }}
    >
      {/* Icon container — sized for SVG glyphs (currentColor = accent) */}
      <div
        aria-hidden="true"
        style={{
          width: 52, height: 52, flexShrink: 0,
          borderRadius: 12, background: 'var(--accent-light)',
          color: 'var(--accent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{
          fontSize: '1.05rem', fontWeight: 600,
          color: 'var(--ink)', marginBottom: 4, lineHeight: 1.3,
        }}>
          {label}
        </div>
        <div style={{
          fontSize: '0.85rem', color: 'var(--ink-muted)',
          lineHeight: 1.4,
        }}>
          {sub}
        </div>
      </div>
    </button>
  )
}

// ─── Audience icons ──────────────────────────────────────────────────────────
//
// Inline SVGs with `currentColor` strokes so they pick up the accent colour
// from the parent. 24×24 viewBox, 1.75 stroke for a slightly more confident
// line weight than the typical 1.5 (reads better at the larger 28px display
// size we use inside the audience buttons).

function BookkeeperIcon() {
  // Document with three checked list items — represents the bookkeeper
  // ticking off transactions as receipts come in.
  return (
    <svg
      width="28" height="28" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="1.75"
      strokeLinecap="round" strokeLinejoin="round"
    >
      {/* Document outline */}
      <path d="M7 3h8l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      {/* Folded corner */}
      <path d="M15 3v4h4" />
      {/* Three checked list items */}
      <path d="M9.5 11.5l1 1 2-2.5" />
      <path d="M14 11h2.5" />
      <path d="M9.5 15.5l1 1 2-2.5" />
      <path d="M14 15h2.5" />
    </svg>
  )
}

function ClientIcon() {
  // Envelope with an upload arrow on top — represents the client
  // receiving an email and uploading receipts in response.
  return (
    <svg
      width="28" height="28" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="1.75"
      strokeLinecap="round" strokeLinejoin="round"
    >
      {/* Envelope body */}
      <rect x="3" y="7" width="18" height="13" rx="1.5" />
      {/* Envelope flap (closed, V shape) */}
      <path d="M3 8.5l9 5.5 9-5.5" />
      {/* Upload arrow on top of the envelope */}
      <path d="M12 2v4.5" />
      <path d="M9.5 4.5L12 2l2.5 2.5" />
    </svg>
  )
}

function FAQSection({ id, eyebrow, heading, items, background }) {
  return (
    <section
      id={id}
      style={{
        padding: '56px 0',
        background,
        scrollMarginTop: 80, // accounts for sticky navbar when scrolled to
      }}
    >
      <div className="container" style={{ padding: '0 24px', maxWidth: 760 }}>
        <div style={{ marginBottom: 32, textAlign: 'center' }}>
          <p style={{
            fontSize: '0.78rem', fontWeight: 600,
            color: 'var(--accent)', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: 10,
          }}>
            {eyebrow}
          </p>
          <h2 style={{
            lineHeight: 1.25,
            fontSize: 'clamp(1.5rem, 4vw, 1.9rem)',
            maxWidth: 600, margin: '0 auto',
          }}>
            {heading}
          </h2>
        </div>

        <div style={{ display: 'grid', gap: 8 }}>
          {items.map(({ q, a }, i) => (
            <FAQItem key={i} q={q} a={a} />
          ))}
        </div>
      </div>
    </section>
  )
}

// Expand/collapse for each question. Default closed to keep the page scannable.
function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{
      borderBottom: '1px solid var(--surface-2)',
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          background: 'none',
          border: 'none',
          padding: '18px 4px',
          cursor: 'pointer',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          fontFamily: 'inherit',
          color: 'var(--ink)',
        }}
        aria-expanded={open}
      >
        <span style={{
          fontSize: '0.98rem', fontWeight: 600,
          color: 'var(--ink)', lineHeight: 1.4,
        }}>
          {q}
        </span>
        <span style={{
          flexShrink: 0,
          fontSize: '1.1rem',
          color: 'var(--ink-muted)',
          marginTop: 2,
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
        }}>
          ⌄
        </span>
      </button>
      {open && (
        <div style={{
          padding: '0 4px 20px',
          fontSize: '0.93rem',
          color: 'var(--ink-muted)',
          lineHeight: 1.65,
        }}>
          {a}
        </div>
      )}
    </div>
  )
}
